import type { CoverageOptionDto } from '../../../../../api/submissions/submission-coverages.types';
import type { ProductType } from '../../../../../types';
import { COVERAGE_PART_TO_COMPONENT_MAP } from './rater-x/helpers';
import { getProductLabel } from './productLabel';

type Parts = CoverageOptionDto['coverages'];

type PolicyValue = {
  props?: {
    limit?: { value?: unknown };
    deductible?: { value?: unknown };
  };
};

/** The pieces of an add-to-cart body that describe limits. Everything else is ignored. */
export type TermsForParts = {
  aggregateLimit?: unknown;
  deductible?: unknown;
  policyValue?: PolicyValue;
  components?: Record<string, { policyValue?: PolicyValue }>;
};

// Read on demand: this module and rater-x/helpers import each other through the cart hook, so the
// map is not yet defined while modules load.
const partNameFor = (componentKey: string): string => {
  const entry = Object.entries(COVERAGE_PART_TO_COMPONENT_MAP).find(
    ([, component]) => component === componentKey
  );
  return entry ? entry[0] : getProductLabel(componentKey as never);
};

const toNumber = (value: unknown): number | null => {
  if (value == null || value === '') {
    return null;
  }
  const number = Number(value);
  return Number.isFinite(number) ? number : null;
};

const toPart = (name: string, limit: unknown, retention: unknown) => ({
  part: { value: name, confidence: 100 },
  limit: { value: toNumber(limit), confidence: 100 },
  retention: { value: toNumber(retention), confidence: 100 },
});

type Context = {
  readonly productType: ProductType;
  /** The name of the one part of a quote with no parts: the seed option's, or the product line's. */
  readonly singlePartName?: string;
};

/**
 * The parts a saved option shows on the Coverages tab, read from the terms the cart add sends:
 * one part per component on a quote made of parts, else one part holding the aggregate limit.
 * Returns none when the terms carry no limit, which makes the option partial rather than wrong.
 */
export const buildOptionParts = (
  terms: TermsForParts,
  context: Context
): Parts => {
  const components = Object.entries(terms.components ?? {});
  if (components.length > 0) {
    return components.map(([key, component]) =>
      toPart(
        partNameFor(key),
        component.policyValue?.props?.limit?.value,
        component.policyValue?.props?.deductible?.value
      )
    );
  }

  const limit = terms.aggregateLimit ?? terms.policyValue?.props?.limit?.value;
  const retention =
    terms.deductible ?? terms.policyValue?.props?.deductible?.value;
  if (toNumber(limit) == null) {
    return [];
  }
  return [
    toPart(
      context.singlePartName ?? getProductLabel(context.productType),
      limit,
      retention
    ),
  ];
};
